import { Button as ChakraButton } from '@web/components/atoms/Button'
import { CircleTickIcon, Icon } from '@web/components/atoms/Icons'
import { Box, HStack, VStack } from '@web/components/atoms/Layout'
import { Body2, Section2 } from '@web/components/atoms/Typography'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import ExtraFields from '@web/components/page-components/ExtraFields'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import {
  AddressInput,
  PostDonationFormSubcomponents,
  PostDonationForm as PostDonationFormProps,
} from '@web/graphql-components'
import { useState } from 'react'
import { getButtonColors } from '../Button/Button'
import { SubComponentProps } from '../utils'
import { getMissingRequiredPostDonationFields, requiredFieldsMessage } from './requiredFields'

type PostDonationFormComponentProps = PostDonationFormProps & {
  interactive?: boolean
  submitted?: boolean
  googleMapsApiKey?: string
  onSubmit?: (values: { [key: string]: string }, address?: AddressInput | null) => Promise<void> | void
  SubComponent?: React.FC<SubComponentProps>
}

const PostDonationForm = (props: PostDonationFormComponentProps) => {
  const {
    id,
    buttonText,
    styles,
    mobileStyles,
    interactive = false,
    submitted = false,
    googleMapsApiKey,
    onSubmit,
    SubComponent,
    subComponents,
    ...rest
  } = props
  const titleId = (subComponents as PostDonationFormSubcomponents | null | undefined)?.title
  const [values, setValues] = useState<{ [key: string]: string }>({})
  const [address, setAddress] = useState<AddressInput | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const extraFields = props.extraFields ?? []
  const { buttonColors, cornerRadius, font } = useBrandingsStore(({ buttonData }) => buttonData)
  const primary = buttonColors.Primary
  const useDefaultBranding = styles?.useDefaultBranding !== false
  const cardBackground = useColorWithOpacity(styles?.backgroundColor, styles?.backgroundColorOpacity, '#E2E8F0')
  const backgroundColor = useColorWithOpacity(
    useDefaultBranding ? null : (styles?.submitButtonBackgroundColor ?? null),
    useDefaultBranding ? null : (styles?.submitButtonBackgroundColorOpacity ?? null),
    primary.bgColor,
  )
  const textColor = useColorWithOpacity(
    useDefaultBranding ? null : (styles?.submitButtonTextColor ?? null),
    '1',
    primary.textColor ?? '#FFFFFF',
  )
  const hoverBackgroundColor = useColorWithOpacity(
    useDefaultBranding ? null : (styles?.submitButtonHoverBackgroundColor ?? null),
    useDefaultBranding ? null : (styles?.submitButtonHoverBackgroundColorOpacity ?? null),
    primary.hoverBgColor,
  )
  const hoverTextColor = useColorWithOpacity(
    useDefaultBranding ? null : (styles?.submitButtonHoverTextColor ?? null),
    '1',
    primary.hoverTextColor ?? '#FFFFFF',
  )
  const borderColor = useColorWithOpacity(null, null, primary.outlineColor ?? primary.bgColor)
  const hoverBorderColor = useColorWithOpacity(null, null, primary.hoverOutlineColor ?? primary.hoverBgColor)
  const buttonRadius = useDefaultBranding ? cornerRadius : (styles?.submitButtonBorderRadius ?? cornerRadius)

  const handleSubmit = async () => {
    if (!interactive || !onSubmit || isSubmitting || submitted) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    try {
      await onSubmit(values, address)
    } catch {
      setSubmitError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      display="flex"
      w="100%"
      css={{
        justifyContent: mobileStyles?.alignSelf ?? 'center',
        '@container page (min-width: 600px)': {
          justifyContent: styles?.alignSelf ?? 'center',
        },
      }}
    >
      <Box
        id={id}
        className={`u-${id}`}
        w="100%"
        bg={cardBackground}
        borderRadius={`${styles?.borderRadius ?? 15}px`}
        css={{
          p: mobileStyles?.padding ?? '20px',
          '@container page (min-width: 600px)': {
            p: styles?.padding ?? '24px',
          },
        }}
      >
        {!submitted && styles?.hasTitle && SubComponent && titleId ? (
          <SubComponent {...rest} id={titleId} name="Title" parentId={id} />
        ) : null}
        <Box
          as="form"
          display="flex"
          flexDirection="column"
          gap="16px"
          w="100%"
          alignItems="stretch"
          pointerEvents={interactive ? 'auto' : 'none'}
          onSubmit={(event) => {
            event.preventDefault()
            if (!interactive || submitted) {
              return
            }

            const missing = getMissingRequiredPostDonationFields(extraFields, values)
            if (missing.length > 0) {
              setSubmitError(requiredFieldsMessage(missing))
              return
            }

            void handleSubmit()
          }}
        >
          {submitted ? (
            <HStack gap="12px" alignItems="center" aria-live="polite">
              <Icon boxSize="24px">
                <CircleTickIcon />
              </Icon>
              <VStack alignItems="start" gap="4px">
                <Section2>{styles?.submittedTitle || "You're all set"}</Section2>
                <Body2 color="ge.text.secondary">
                  {styles?.submittedMessage || 'Thanks for taking the time to share this with us.'}
                </Body2>
              </VStack>
            </HStack>
          ) : (
            <>
              {extraFields.length === 0 ? (
                interactive ? null : (
                  <Body2 color="ge.text.secondary">Add fields in the Data tab</Body2>
                )
              ) : (
                <ExtraFields
                  fields={extraFields}
                  googleMapsApiKey={googleMapsApiKey}
                  address={address}
                  onAddressChange={(value) =>
                    setAddress(
                      value
                        ? {
                            addressLine1: value.addressLine1 ?? '',
                            addressLine2: value.addressLine2 ?? '',
                            city: value.city ?? '',
                            state: value.state ?? '',
                            postcode: value.postcode ?? '',
                            country: value.country ?? '',
                          }
                        : null,
                    )
                  }
                  {...(interactive
                    ? {
                        values,
                        onChange: (name: string, value: string) =>
                          setValues((current) => ({ ...current, [name]: value })),
                      }
                    : {})}
                />
              )}
              <ChakraButton
                id={`${id}-submit`}
                type="submit"
                buttonAppearance="unstyled"
                variant={primary.variant}
                borderRadius={buttonRadius || 4}
                fontFamily={font?.font}
                fontWeight={600}
                alignSelf={styles?.submitButtonAlign ?? 'start'}
                w={`${styles?.submitButtonWidth ?? 100}%`}
                height="50px"
                fontSize="18px"
                disabled={isSubmitting}
                {...getButtonColors(backgroundColor, textColor, borderColor, primary.variant)}
                _hover={getButtonColors(hoverBackgroundColor, hoverTextColor, hoverBorderColor, primary.variant)}
              >
                {isSubmitting ? 'Submitting...' : buttonText || 'Submit'}
              </ChakraButton>
              {submitError ? (
                <Body2 color="red.600" aria-live="polite">
                  {submitError}
                </Body2>
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default PostDonationForm
